/**
 * HUB-B.2 — Actions ventes Historique / Centre Activité (handlers + état modales).
 */
import { useState, useCallback, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import { printTicket80mm, openTicketPdf } from '../../utils/ticket80mmPrint';

export default function useHistoriqueVenteActions({ api, onRefresh, testIdPrefix = 'historique' }) {
  const { tError } = useLanguage();

  const [convertingRef, setConvertingRef] = useState(null);
  const [showRetraitModal, setShowRetraitModal] = useState(false);
  const [retraitData, setRetraitData] = useState({ montant: 0, motif: '' });
  const [emailModal, setEmailModal] = useState({ show: false, ref: '', email: '' });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [paymentModal, setPaymentModal] = useState({ show: false, ref: '', current: '' });
  const [savingPayment, setSavingPayment] = useState(false);
  const [factureDefModal, setFactureDefModal] = useState({ show: false, vente: null, pdfBlob: null, loading: false });
  const [defEmailModal, setDefEmailModal] = useState({ show: false, deviceRef: '', email: '' });
  const [sendingDefEmail, setSendingDefEmail] = useState(false);
  const [completerModal, setCompleterModal] = useState({ show: false, vente: null });
  const [completerData, setCompleterData] = useState({ mode_paiement: 'Espèces', paiement_especes: '', paiement_cb: '' });
  const [completingPayment, setCompletingPayment] = useState(false);
  const [avoirModal, setAvoirModal] = useState({ show: false, vente: null });

  const handlePrintTicket = useCallback(async (ref) => {
    try {
      await printTicket80mm(api, { type: 'vente', ref });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur impression ticket');
    }
  }, [api, tError]);

  const handleViewTicketPdf = useCallback(async (ref) => {
    try {
      await openTicketPdf(api, { type: 'vente', ref });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || error.message || 'Erreur ouverture ticket PDF');
    }
  }, [api, tError]);

  const handleSendEmail = useCallback(async () => {
    if (!emailModal.email || !emailModal.email.includes('@')) {
      alert('Adresse email invalide');
      return;
    }
    setSendingEmail(true);
    try {
      await api.post('/ventes/send-invoice-email', {
        email: emailModal.email,
        ref_vente: emailModal.ref,
      });
      alert(`Facture envoyée à ${emailModal.email}`);
      setEmailModal({ show: false, ref: '', email: '' });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur envoi email');
    } finally {
      setSendingEmail(false);
    }
  }, [api, emailModal, tError]);

  const handleSendDefEmail = useCallback(async () => {
    if (!defEmailModal.email || !defEmailModal.email.includes('@')) {
      alert('Adresse email invalide');
      return;
    }
    setSendingDefEmail(true);
    try {
      await api.post(`/factures/definitive/${encodeURIComponent(defEmailModal.deviceRef)}/email`, {
        email: defEmailModal.email,
      });
      alert(`Facture définitive envoyée à ${defEmailModal.email}`);
      setDefEmailModal({ show: false, deviceRef: '', email: '' });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur envoi email');
    } finally {
      setSendingDefEmail(false);
    }
  }, [api, defEmailModal, tError]);

  const handleCompleterPaiement = useCallback(async () => {
    const vente = completerModal.vente;
    if (!vente) return;
    const esp = parseFloat(completerData.paiement_especes) || 0;
    const cb = parseFloat(completerData.paiement_cb) || 0;
    const montant = esp + cb;
    if (montant <= 0) {
      alert('Montant invalide');
      return;
    }
    setCompletingPayment(true);
    try {
      const res = await api.post(`/ventes/${encodeURIComponent(vente.ref_vente)}/completer-paiement`, {
        mode_paiement: completerData.mode_paiement,
        paiement_especes: esp || null,
        paiement_cb: cb || null,
      });
      alert(`Paiement complété : ${res.data.complement?.toFixed(2)} EUR\nReste à payer : ${res.data.reste_a_payer?.toFixed(2)} EUR`);
      setCompleterModal({ show: false, vente: null });
      setCompleterData({ mode_paiement: 'Espèces', paiement_especes: '', paiement_cb: '' });
      onRefresh?.();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur');
    } finally {
      setCompletingPayment(false);
    }
  }, [api, completerModal, completerData, onRefresh, tError]);

  const handleRetrait = useCallback(async () => {
    if (retraitData.montant <= 0 || !retraitData.motif) {
      alert('Montant et motif requis');
      return;
    }
    try {
      const response = await api.post('/ventes/retrait', retraitData);
      setShowRetraitModal(false);
      setRetraitData({ montant: 0, motif: '' });
      onRefresh?.();
      if (response?.data?.ref_vente) {
        await handlePrintTicket(response.data.ref_vente);
      }
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur lors du retrait');
    }
  }, [api, retraitData, onRefresh, handlePrintTicket, tError]);

  const handleDelete = useCallback(async (refVente) => {
    if (!window.confirm(`Supprimer le ticket ${refVente} ?`)) return;
    try {
      const res = await api.delete(`/ventes/${encodeURIComponent(refVente)}`);
      const restored = res?.data?.stock_restored ?? 0;
      if (restored > 0) {
        alert(`Ticket supprimé — ${restored} article(s) remis en stock.`);
      }
      onRefresh?.();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur lors de la suppression');
    }
  }, [api, onRefresh, tError]);

  const handleChangePayment = useCallback((vente) => {
    setPaymentModal({ show: true, ref: vente.ref_vente, current: vente.mode_paiement || 'Carte Bancaire' });
  }, []);

  const submitPaymentChange = useCallback(async () => {
    if (!paymentModal.current) return;
    setSavingPayment(true);
    try {
      await api.put(`/ventes/${encodeURIComponent(paymentModal.ref)}/mode-paiement`, {
        mode_paiement: paymentModal.current,
      });
      setPaymentModal({ show: false, ref: '', current: '' });
      onRefresh?.();
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur lors de la mise à jour du paiement');
    } finally {
      setSavingPayment(false);
    }
  }, [api, paymentModal, onRefresh, tError]);

  const handleConvertToInvoice = useCallback(async (refVente) => {
    if (!window.confirm(`Transformer le ticket ${refVente} en facture PDF ?`)) return;
    try {
      setConvertingRef(refVente);
      const response = await api.post(`/ventes/${encodeURIComponent(refVente)}/facture`, {}, {
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (printWindow) printWindow.focus();
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur lors de la génération de la facture');
    } finally {
      setConvertingRef(null);
    }
  }, [api, tError]);

  const handleFactureAcompte = useCallback(async (vente) => {
    try {
      setConvertingRef(vente.ref_vente);
      const acompteRes = await api.get(`/acomptes?device_ref=${encodeURIComponent(vente.device_ref || '')}`);
      const acomptes = acompteRes.data || [];
      const matching = acomptes.find((a) => a.ref_acompte === vente.ref_vente);
      if (!matching) {
        alert('Acompte non trouvé');
        return;
      }
      const response = await api.post(`/factures/acompte/${matching.id}`, {}, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank', 'width=900,height=700');
      if (printWindow) printWindow.focus();
      setTimeout(() => window.URL.revokeObjectURL(url), 8000);
    } catch (error) {
      alert(tError(error.response?.data?.detail) || "Erreur lors de la génération de la facture d'acompte");
    } finally {
      setConvertingRef(null);
    }
  }, [api, tError]);

  const handleFactureDefinitive = useCallback(async (vente) => {
    const deviceRef = vente.device_ref || '';
    if (!deviceRef) {
      alert('Réf. de réparation non trouvée');
      return;
    }
    try {
      setConvertingRef(vente.ref_vente);
      const statusRes = await api.get(`/factures/repair-payment-status/${encodeURIComponent(deviceRef)}`);
      const status = statusRes.data;
      if (!status.is_fully_paid) {
        if (!window.confirm(`La réparation n'est pas totalement payée (${status.total_acomptes.toFixed(2)} / ${status.total_repair.toFixed(2)} €). Générer quand même la facture définitive ?`)) {
          return;
        }
      }
      const response = await api.post(`/factures/definitive/${encodeURIComponent(deviceRef)}`, {}, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      setFactureDefModal({ show: true, vente, pdfBlob: blob, loading: false });
    } catch (error) {
      alert(tError(error.response?.data?.detail) || 'Erreur lors de la génération de la facture définitive');
    } finally {
      setConvertingRef(null);
    }
  }, [api, tError]);

  const openCompleterModal = useCallback((vente) => {
    setCompleterModal({ show: true, vente });
    setCompleterData({
      mode_paiement: 'Espèces',
      paiement_especes: String(vente.reste_a_payer || 0),
      paiement_cb: '',
    });
  }, []);

  const actionHandlers = useMemo(() => ({
    onPrintTicket: handlePrintTicket,
    onViewTicketPdf: handleViewTicketPdf,
    onCompleter: openCompleterModal,
    onFactureAcompte: handleFactureAcompte,
    onFactureDefinitive: handleFactureDefinitive,
    onDefEmail: (vente) => setDefEmailModal({ show: true, deviceRef: vente.device_ref, email: vente.client_email || '' }),
    onConvertInvoice: handleConvertToInvoice,
    onEmail: (vente) => setEmailModal({
      show: true,
      ref: vente.ref_vente,
      email: vente.client_email || '',
      total: vente.prix_total ?? vente.total ?? 0,
      montant_paye: vente.montant_paye ?? vente.prix_total ?? vente.total ?? 0,
      reste_a_payer: vente.reste_a_payer ?? 0,
      mode_paiement: vente.mode_paiement || '',
    }),
    onChangePayment: handleChangePayment,
    onConvertAvoir: (vente) => setAvoirModal({ show: true, vente }),
    onDelete: handleDelete,
  }), [
    handlePrintTicket, handleViewTicketPdf, openCompleterModal, handleFactureAcompte, handleFactureDefinitive,
    handleConvertToInvoice, handleChangePayment, handleDelete,
  ]);

  return {
    convertingRef,
    actionHandlers,
    testIdPrefix,
    modals: {
      showRetraitModal,
      setShowRetraitModal,
      retraitData,
      setRetraitData,
      handleRetrait,
      emailModal,
      setEmailModal,
      sendingEmail,
      handleSendEmail,
      defEmailModal,
      setDefEmailModal,
      sendingDefEmail,
      handleSendDefEmail,
      paymentModal,
      setPaymentModal,
      savingPayment,
      submitPaymentChange,
      factureDefModal,
      setFactureDefModal,
      completerModal,
      setCompleterModal,
      completerData,
      setCompleterData,
      completingPayment,
      handleCompleterPaiement,
      avoirModal,
      setAvoirModal,
      handleAvoirCreated: (avoir) => {
        alert(`Avoir créé : ${avoir.ref} · ${Number(avoir.solde).toFixed(2)} €`);
        onRefresh?.();
        if (avoir?.ref) {
          printTicket80mm(api, { type: 'avoir', ref: avoir.ref }).catch(() => {});
        }
      },
    },
  };
}
